import { Injectable } from '@angular/core';
import { ApolloLink, InMemoryCache, WatchQueryFetchPolicy } from '@apollo/client';
import { APOLLO_OPTIONS, Apollo } from 'apollo-angular';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { environment } from '../environments/environment';
import { createScApolloLink } from './common/api/CreateScApolloLink';
import { createTypePolicyFields } from './common/api/create-type-policy-fields';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './common/services/auth.service';
import { CookieService } from 'ngx-cookie';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class GraphqlProvider {
  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private cookies: CookieService,
    private toastr: ToastrService
  ) {}

  createApollo() {
    const errorLink = new ErrorLink(({ error, operation }) => {
      const { muteError } = operation.getContext();
      if (muteError) {
        return;
      }

      if (CombinedGraphQLErrors.is(error)) {
        error.errors.forEach(({ message, locations, path }) => this.toastr.error(message));
      } else if (error) {
        this.toastr.error(error.message);
      } else {
        console.error(`[Network error]: ${error}`);
        this.toastr.error('Something went wrong');
      }
    });

    const isDevMode: boolean = !environment.production;

    return {
      link: ApolloLink.from([
        errorLink,
        createScApolloLink({
          fetchUri: environment.endpoints.gqlv3,
          httpClient: this.httpClient,
          authService: this.authService,
          cookies: this.cookies
        })
      ]),
      cache: new InMemoryCache({
        possibleTypes: {
          AnyStepType: ['QuestionnaireStepType', 'CaseComponentType']
        },
        typePolicies: {
          Query: {
            fields: createTypePolicyFields()
          },
          CaseComponentType: {
            // This ensures duplicate case components will keep their unique "sets" values
            // All duplicates inherit the first component's "sets" value if we don't check for it here
            keyFields: (object, context) => {
              const fields = ['id'];
              if (object.hasOwnProperty('sets')) {
                fields.push('sets');
              }

              return fields;
            }
          }
        }
      }),
      devtools: { enabled: isDevMode },
      defaultOptions: {
        watchQuery: {
          // Casting here fixes the TS2345 error
          fetchPolicy: 'cache-and-network' as WatchQueryFetchPolicy
        }
      }
    };
  }
}

export const graphqlProvider = [
  GraphqlProvider, // Provide the class itself
  Apollo, // Provide the Apollo service engine
  {
    provide: APOLLO_OPTIONS,
    useFactory: (graphQLProvider: GraphqlProvider) => graphQLProvider.createApollo(),
    deps: [GraphqlProvider]
  }
];
